import {
	AVAILABLE_VERSION_BY_TEMPLATE_TYPE,
	FilePreviewMode,
	GeneratableDocumentVersion,
} from '@dealstack/shared/lib/common/business/generatableDocuments.ts';
import React, { lazy, ReactNode, Suspense, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, Divider, Flex, Radio, Text } from '@mantine/core';
import type { GeneratableDocumentWorkflowTemplateType } from '@dealstack/shared/lib/generated';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import styled from 'styled-components';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { ManageCustomDetailsDefinitionsModal } from '@dealstack/frontend/admin/pages/Manage/common/ManageCustomDetailsDefinitionsModal/ManageCustomDetailsDefinitionsModal';
import {
	SmartFieldInForm,
	useSmartFieldsController,
} from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/SmartFieldsTab/useSmartFieldsController.ts';
import { useTemplatePreviewController } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/SmartFieldsTab/useTemplatePreviewController.ts';
import { EditFieldMappingModal } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/EditFieldMappingModal.tsx';
import { FormProvider } from 'react-hook-form';
import { mapCustomDetailsDefinitionToSource } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/availableSources.ts';
import { useDocumentCustomDetailsDefinitions as useGdwtCustomDetailsDefinitions } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/SmartFieldsTab/useDocumentCustomDetailsDefinitions.ts';
import { useSmartFieldsHighlight } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/SmartFieldsTab/useSmartFieldsHighlight.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';

const PdfViewer = lazy(() =>
	import('@dealstack/frontend/shared/components/DocumentViewerV2/PdfViewer/PdfViewer').then(
		(m) => ({ default: m.PdfViewer })
	)
);

interface Props {
	templateId: string;
	templateName: string;
	templateType: GeneratableDocumentWorkflowTemplateType;
}

export const SmartFieldsTab = ({ templateName, templateType, templateId }: Props) => {
	const [selectedSmartField, setSelectedSmartField] = useState<SmartFieldInForm | null>(null);

	const { smartFields, smartFieldForm, onSmartFieldsSubmit } =
		useSmartFieldsController(templateId);

	const {
		fileBlob,
		fileName,
		selectedVersion,
		setSelectedVersion,
		filePreviewMode,
		setFilePreviewMode,
		isFileLoading,
	} = useTemplatePreviewController(smartFields, templateType);

	const {
		onCustomDetailsDefinitionsSubmit,
		customDetailsDefinitions,
		showManageCustomDetailsDefinitionsModal,
		setShowManageCustomDetailsDefinitionsModal,
	} = useGdwtCustomDetailsDefinitions(templateId);

	const customDetailsDefinitionsSources = useMemo(() => {
		return customDetailsDefinitions?.map(mapCustomDetailsDefinitionToSource);
	}, [customDetailsDefinitions]);

	const { pdfViewerRef, higlightSmartFields, generatedFileNameField } = useSmartFieldsHighlight(
		smartFieldForm.watch('smartFields'),
		customDetailsDefinitionsSources,
		setSelectedSmartField
	);

	const previewVersions = AVAILABLE_VERSION_BY_TEMPLATE_TYPE[templateType];
	const showPreviewVersionInput = previewVersions.length > 1;

	let fileTitle: string | ReactNode = '';
	if (isFileLoading) {
		fileTitle = '';
	} else if (filePreviewMode === FilePreviewMode.MOCKED_DATA) {
		fileTitle = fileName;
	} else {
		fileTitle = (
			<Text
				className={`smartField ${generatedFileNameField?.isValid ? 'smartField-valid' : ''}`}
				onClick={() => {
					setSelectedSmartField(generatedFileNameField as SmartFieldInForm);
				}}
			>
				{`{{${generatedFileNameField?.fieldKey}}`}
			</Text>
		);
	}

	return (
		<Flex gap="1rem">
			<Flex direction="column" gap="1rem" sx={{ minWidth: '22rem', width: '22rem' }}>
				<Button onClick={onSmartFieldsSubmit}>Save changes</Button>

				<ContainerWithHeader
					icon={<Icon variant="settings" size="xl" />}
					title="File view settings"
				>
					<Flex direction="column" gap="1rem">
						{showPreviewVersionInput && (
							<Radio.Group
								value={selectedVersion}
								onChange={(v: GeneratableDocumentVersion) => setSelectedVersion(v)}
								label={<Text size="B6Bold">Version:</Text>}
							>
								<Flex gap="1rem" mt="0.5rem">
									<Radio
										value={GeneratableDocumentVersion.INDIVIDUAL}
										label="Individual"
									/>
									<Radio value={GeneratableDocumentVersion.PIV} label="PIV" />
								</Flex>
							</Radio.Group>
						)}

						<Radio.Group
							value={filePreviewMode}
							onChange={(v: FilePreviewMode) => setFilePreviewMode(v)}
							label={<Text size="B6Bold">File view mode:</Text>}
						>
							<Flex gap="1rem" mt="0.5rem">
								<Radio value={FilePreviewMode.SMART_FIELDS} label="Smart fields" />
								<Radio value={FilePreviewMode.MOCKED_DATA} label="Mocked data" />
							</Flex>
						</Radio.Group>
					</Flex>
				</ContainerWithHeader>

				<ContainerWithHeader
					icon={<Icon variant="document-v3" size="xl" />}
					title="Smart fields"
				>
					<Flex direction="column" gap="1rem" sx={{ overflow: 'auto', flexGrow: 1 }}>
						<Button onClick={() => setShowManageCustomDetailsDefinitionsModal(true)}>
							Manage custom details
						</Button>

						<Divider />

						{smartFields.map((field) => (
							<Flex key={field.fieldKey} gap="0.5rem" align="center">
								<Icon variant="dot" />
								<Text size="B5Medium">{field.fieldKey}</Text>
							</Flex>
						))}
					</Flex>
				</ContainerWithHeader>
			</Flex>

			<Suspense fallback={<InlineLoader />}>
				<StyledPdfViewer
					file={fileBlob}
					fileName={templateName}
					fileTitleComponent={fileTitle}
					isLoading={isFileLoading}
					renderTextLayer
					ref={pdfViewerRef}
					pageProps={{ onRenderTextLayerSuccess: higlightSmartFields }}
				/>
			</Suspense>

			{showManageCustomDetailsDefinitionsModal && (
				<ManageCustomDetailsDefinitionsModal
					opened={showManageCustomDetailsDefinitionsModal}
					onClose={() => setShowManageCustomDetailsDefinitionsModal(false)}
					initialCustomDefinitions={customDetailsDefinitions ?? []}
					onSubmit={onCustomDetailsDefinitionsSubmit}
				/>
			)}

			<FormProvider {...smartFieldForm}>
				<EditFieldMappingModal
					opened={!!selectedSmartField}
					onClose={() => setSelectedSmartField(null)}
					fieldKey={selectedSmartField?.fieldKey}
					fieldPath={`smartFields.${smartFields.findIndex((field) => field.id === selectedSmartField?.id)}`}
					customDetailsDefinitionsSources={customDetailsDefinitionsSources}
				/>
			</FormProvider>
		</Flex>
	);
};

const StyledPdfViewer = styled(PdfViewer)`
	.react-pdf__Page {
		border: 1px solid ${defaultTheme.v3.colors.gray[3]};
	}

	.smartField {
		border: 1px solid ${defaultTheme.v3.colors.blue[6]};
		background-color: transparent;
		position: relative;
		font-family: inherit;
		cursor: pointer;
	}

	.smartField-valid {
		border: 1px solid ${defaultTheme.v3.colors.green[6]};
	}

	.smartField-invalid {
		border: 1px solid ${defaultTheme.v3.colors.red[6]};
	}
`;
